<template>
  <div class="flex-1 w-full max-w-[552px] min-h-[calc(var(--app-height))] mx-auto">
    <Toast position="top-[48px]" />
    <form ref="form" @submit.prevent="submit">
      <div
        class="py-0 flex flex-col justify-between items-start w-full min-h-[calc(var(--app-height)-120px)]"
      >
        <div class="w-full">
          <h1 class="text-text-display-lg font-black text-brown-800 tracking-[-0.03em]">
            Create a password
          </h1>
          <div class="pt-[40px] w-full">
            <DsPassword
              v-model="newPassword"
              label="New password"
              placeholder="Enter password"
              :error="errors.newPassword"
            />
          </div>

          <div class="w-full pt-[12px]">
            <PasswordConditions
              :has-eight-characters="hasEightCharacters"
              :has-one-lower-case="hasOneLowerCase"
              :has-one-upper-case="hasOneUpperCase"
              :has-one-number="hasOneNumber"
              :has-one-symbol="hasOneSymbol"
            />
          </div>

          <div class="pt-[40px] w-full">
            <DsPassword
              v-model="confirmPassword"
              label="Confirm your password"
              placeholder="Enter password"
              :error="passwordsDoNotMatch ? 'Passwords do not match' : undefined"
            />
          </div>
        </div>

        <div
          class="mt-[24px] w-full"
          :style="hasFocusedField ? `padding-bottom: ${keyboardInsetCss}` : ''"
        >
          <BottomButton
            text="Create Password"
            :disabled="!canSave || loading || isSubmitting"
            :loading="isSubmitting"
            class="w-full md:w-[552px]"
          />
        </div>
      </div>
    </form>
  </div>
</template>
<script>
import { Password as DsPassword } from '@team-hogc/hogc-one-design-system';
import BottomButton from '@/components/common/BottomButton.vue';
import PasswordConditions from '@/components/common/PasswordConditions.vue';
import Toast from '@/components/common/Toast.vue';
import { mapState } from 'vuex';
import keyboardFieldIntoView from '@/mixins/keyboard-field-into-view';

export default {
  mixins: [keyboardFieldIntoView],
  components: {
    BottomButton,
    DsPassword,
    PasswordConditions,
    Toast,
  },

  data() {
    return {
      newPassword: '',
      confirmPassword: '',
      errors: {
        currentPassword: null,
        newPassword: null,
      },
      isSubmitting: false,
    };
  },
  computed: {
    ...mapState('registration', ['loading']),
    passwordValue() {
      return this.newPassword;
    },

    confirmPasswordValue() {
      return this.confirmPassword;
    },

    hasEightCharacters() {
      return this.passwordValue.length >= 8;
    },

    hasOneUpperCase() {
      return /[A-Z]/.test(this.passwordValue);
    },

    hasOneLowerCase() {
      return /[a-z]/.test(this.passwordValue);
    },

    hasOneNumber() {
      return /\d/.test(this.passwordValue);
    },
    hasOneSymbol() {
      return /[^a-zA-Z0-9\s]/.test(this.passwordValue);
    },

    passwordsDoNotMatch() {
      // if either field is not yet filled, don't throw an error
      if (!this.passwordValue || !this.confirmPasswordValue) {
        return false;
      }

      // both fields are filled, thus throw an error if they don't match
      return this.passwordValue !== this.confirmPasswordValue;
    },

    canSave() {
      return (
        this.hasEightCharacters &&
        this.hasOneUpperCase &&
        this.hasOneLowerCase &&
        this.hasOneNumber &&
        this.hasOneSymbol &&
        this.passwordValue &&
        this.confirmPasswordValue &&
        !this.passwordsDoNotMatch
      );
    },
  },
  mounted() {
    this.startKeyboardOverlapTracking();
  },
  methods: {
    async submit() {
      if (this.isSubmitting) return;
      this.isSubmitting = true;
      try {
        await this.$store.dispatch('registration/registerUser', this.newPassword);
        this.$emit('next');
      } catch (e) {
        console.error('Error resetting password:', e);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>
